import React, { useEffect, useState } from 'react'
import {
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography
} from '@material-ui/core'
import { Box } from '@mui/material'
import { useHistory, useLocation } from 'react-router-dom'
import subjectSlice, {
  deleteSubjectById,
  getSubjects,
  sendBatchRemindersToSpeakersForTalks,
  sendInviteEmailToSpecificSubject,
  sendInviteEmailToSubjects,
  sendInviteOrReminderEmailToAttendees
} from 'store/subject'
import { useDispatch, useSelector } from 'react-redux'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import Wrapper from 'components/Wrapper'
import getTableOptions from 'utils/getTableOptions'
import ConfirmModal from 'components/ConfirmationModal'
import { DeleteOutlined, EditOutlined, MailOutlined } from '@material-ui/icons'
import { Roles, SpeakerSubjectFilter, SubjectStatus, SubjectType } from 'constants/index'
import {
  areAllValuesZero,
  caseInsensitiveSort,
  getSubjectFilterList,
  getSubjectStatusBasedCount
} from 'utils'
import SubjectCard, { AttendeeSubjectCard } from 'components/SubjectCard'
import { convertTimestampToLocalString } from 'utils/times'
import { fetchMultipleSubjectTalks } from 'store/talk'

const ActionButtons = ({ subject }) => {
  const history = useHistory()
  const location = useLocation()
  const [deletedId, setDeletedId] = useState('')
  const [loading, setLoading] = useState(false)
  const [subjectId, setSubjectId] = useState('')
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openInviteModal, setOpenInviteModal] = useState(false)

  const handleOpenInviteModal = (id) => {
    setOpenInviteModal(true)
    setSubjectId(id)
  }
  const handleCloseInviteModal = () => {
    setOpenInviteModal(false)
    setSubjectId('')
  }

  const handleSendEmailInvite = async () => {
    setLoading(true)
    if ([SubjectStatus.INVITE_NOT_SENT, SubjectStatus.AWAITING_RESPONSE].includes(subject.status)) {
      await sendInviteEmailToSpecificSubject(subjectId, subject?.conferenceId)
    } else {
      await sendBatchRemindersToSpeakersForTalks([subjectId])
    }
    setLoading(false)
  }

  const handleOpenDeleteModal = (id) => {
    setOpenDeleteModal(true)
    setDeletedId(id)
  }

  const handleDeleteSubject = async () => {
    setLoading(true)
    const isDeleted = await deleteSubjectById(deletedId)
    isDeleted && setLoading(false)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setDeletedId('')
  }

  return (
    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
      <ConfirmModal
        dialogTitle="Delete Subject"
        dialogText={
          [SubjectStatus.APPROVED, 'Awaiting talks'].includes(subject?.status) &&
          subject?.type === SubjectType.SPEAKER
            ? `This action will delete approved subject along with the corresponding ${subject.type}, Talks and Polls.`
            : subject?.status === SubjectStatus.APPROVED && subject?.type !== SubjectType.SPEAKER
            ? `This action will delete approved subject along with the corresponding ${subject.type}.`
            : 'Do you really want to delete this subject?'
        }
        open={openDeleteModal}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleDeleteSubject}
        inProgress={loading}
      />
      <ConfirmModal
        dialogTitle={
          subject?.status === SpeakerSubjectFilter.AWAITING_TALKS
            ? 'Speaker Reminder for Talk Submissions'
            : `Send ${
                subject?.status === SubjectStatus.INVITE_NOT_SENT ? 'Invite' : 'Reminder'
              } Email to Subject`
        }
        dialogText={
          subject?.status === SpeakerSubjectFilter.AWAITING_TALKS
            ? 'This will send reminder email to this Speaker to submit their talks.'
            : `This will send ${
                subject?.status === SubjectStatus.INVITE_NOT_SENT ? 'Invite' : 'Reminder'
              } email to this ${subject.type}.`
        }
        open={openInviteModal}
        closeModal={handleCloseInviteModal}
        handleConfirm={handleSendEmailInvite}
        inProgress={loading}
      />
      <Tooltip title="Edit">
        <IconButton
          size="small"
          onClick={() => {
            history.push(`${location.pathname}/edit/${subject.id}`)
          }}>
          <EditOutlined />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" onClick={() => handleOpenDeleteModal(subject.id)}>
          <DeleteOutlined />
        </IconButton>
      </Tooltip>
      {[
        SubjectStatus.INVITE_NOT_SENT,
        SubjectStatus.AWAITING_RESPONSE,
        SpeakerSubjectFilter.AWAITING_TALKS
      ].includes(subject.status) && (
        <Tooltip
          title={
            subject.status === SubjectStatus.AWAITING_RESPONSE ? 'Send Reminder' : 'Send Invitation'
          }>
          <IconButton size="small" onClick={() => handleOpenInviteModal(subject.id)}>
            <MailOutlined />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}
const SubjectsListing = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const options = getTableOptions()
  const [openEmailModal, setOpenEmailModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const { user } = useSelector((state) => state.auth)
  const { inProgress, subjectType, status, confFilter, currentPage, pageSize, subjects } =
    useSelector((state) => state.subjects)
  const {
    conference,
    conferences,
    inProgress: confProgress
  } = useSelector((state) => state.conference)
  const { subjectTalksMap } = useSelector((state) => state.talk)
  const activeConferences = conferences?.filter((row) => row.isActive === true)
  const orderedConferences = caseInsensitiveSort(activeConferences, 'confName')
  const speakerCanUploadTalks =
    user?.role === Roles.ADMIN
      ? conference?.speakerCanUploadTalks
      : conferences?.find((conf) => conf?.id === confFilter)?.speakerCanUploadTalks

  const selectedConference = conferences?.find((conf) => conf?.id === confFilter)
  const confTimezone =
    user?.role === Roles.ADMIN ? conference?.timezone : selectedConference?.timezone
  const conf24HrFormat =
    user?.role === Roles.ADMIN ? conference?.is24hrFormat : selectedConference?.is24hrFormat

  const approvedSpeakers = subjects?.filter(
    (e) => e.status === SubjectStatus.APPROVED && e.type === SubjectType.SPEAKER
  )
  const awaitingForTalks = approvedSpeakers
    ?.filter((e) => !subjectTalksMap.has(e.id))
    .map((e) => ({ ...e, status: SpeakerSubjectFilter.AWAITING_TALKS }))

  const awaitingTalksIds = awaitingForTalks?.map((e) => e?.id)
  const filteredOldSubjects = subjects?.filter((e) => !awaitingTalksIds?.includes(e.id))
  const updatedSubjects = speakerCanUploadTalks
    ? [...filteredOldSubjects, ...awaitingForTalks]
    : subjects

  const exhibitors = updatedSubjects?.filter((subject) => subject.type === SubjectType.EXHIBITOR)
  const speakers = updatedSubjects?.filter((subject) => subject.type === SubjectType.SPEAKER)
  const sponsors = updatedSubjects?.filter((subject) => subject.type === SubjectType.SPONSOR)
  const attendees = updatedSubjects?.filter((subject) => subject.type === SubjectType.ATTENDEE)

  const exhibitorsCount = getSubjectStatusBasedCount(exhibitors, SubjectType.EXHIBITOR)
  const speakersCount = getSubjectStatusBasedCount(speakers, SubjectType.SPEAKER)
  const sponsorsCount = getSubjectStatusBasedCount(sponsors, SubjectType.SPONSOR)
  const attendeesCount = getSubjectStatusBasedCount(attendees, SubjectType.ATTENDEE)

  const subjectCount =
    subjectType === SubjectType.ATTENDEE
      ? attendeesCount
      : subjectType === SubjectType.EXHIBITOR
      ? exhibitorsCount
      : subjectType === SubjectType.SPEAKER
      ? speakersCount
      : sponsorsCount

  const statusList = getSubjectFilterList(subjectType, speakerCanUploadTalks)

  const filteredSubjects =
    status && status === 'All'
      ? updatedSubjects.filter((subject) => subject.type === subjectType)
      : updatedSubjects.filter(
          (subject) => subject.type === subjectType && subject.status === status
        )

  let showInviteEmailButton
  let showReminderEmailButton
  let inviteCount
  let reminderCount

  showInviteEmailButton = subjectCount.pending > 0
  showReminderEmailButton = subjectCount.invited > 0
  inviteCount = subjectCount.pending > 0 && subjectCount.pending
  reminderCount = subjectCount.invited > 0 && subjectCount.invited

  const tableHead = (subjectType) => {
    return [
      'Email',
      {
        name: subjectType === SubjectType.SPONSOR ? 'Company' : 'Full Name',
        options: {
          customBodyRender: (value) => (value ? value : 'N/A')
        }
      },
      'Status',
      {
        name: 'Invite Sent on',
        options: {
          customBodyRender: (value) =>
            value ? (
              <div style={{ minWidth: '130px' }}>
                {convertTimestampToLocalString(value, confTimezone, conf24HrFormat)}
              </div>
            ) : (
              'N/A'
            )
        }
      },
      {
        name: 'Data Received on',
        options: {
          display: subjectType !== SubjectType.ATTENDEE,
          customBodyRender: (value) =>
            value ? (
              <div style={{ minWidth: '130px' }}>
                {convertTimestampToLocalString(value, confTimezone, conf24HrFormat)}
              </div>
            ) : (
              'N/A'
            )
        }
      },
      {
        name: 'Approved on',
        options: {
          display: subjectType !== SubjectType.ATTENDEE,
          customBodyRender: (value) =>
            value ? (
              <div style={{ minWidth: '130px' }}>
                {convertTimestampToLocalString(value, confTimezone, conf24HrFormat)}
              </div>
            ) : (
              'N/A'
            )
        }
      },
      'Actions'
    ]
  }
  const formatTableData = (subjects) =>
    subjects.map((subject) => [
      subject.email,
      subject?.type === SubjectType.EXHIBITOR ? subject.name : subject.fullName,
      subject.status,
      subject?.invitedAt,
      subject?.submittedAt,
      subject?.approvedAt,
      <ActionButtons subject={subject} />
    ])

  const handleOpenEmailModal = () => {
    setOpenEmailModal(true)
  }

  const handleCloseEmailModal = () => {
    setOpenEmailModal(false)
  }

  const handleSendInviteEmails = async () => {
    setLoading(true)
    const conferenceId = user?.role === Roles.SUPER_ADMIN ? confFilter : user?.conferenceId
    if (
      subjectType === SubjectType.ATTENDEE &&
      [SubjectStatus.INVITE_NOT_SENT, SubjectStatus.AWAITING_RESPONSE].includes(status)
    ) {
      await sendInviteOrReminderEmailToAttendees(status, conferenceId)
    } else if (
      subjectType !== SubjectType.ATTENDEE &&
      [SubjectStatus.INVITE_NOT_SENT, SubjectStatus.AWAITING_RESPONSE].includes(status)
    ) {
      await sendInviteEmailToSubjects(subjectType, status, conferenceId)
    } else {
      awaitingTalksIds && (await sendBatchRemindersToSpeakersForTalks(awaitingTalksIds))
    }
    setLoading(false)
  }

  useEffect(() => {
    if (confFilter && user.role === Roles.SUPER_ADMIN && !confProgress) {
      conferences.every((conf) => {
        if (conf.id === confFilter && conf.isActive === false) {
          dispatch(subjectSlice.actions.setConferenceFilter(orderedConferences[0]?.id))
          return false
        }
        return true
      })
    }
  }, [confProgress, status, confFilter])

  useEffect(() => {
    if (user.role === Roles.SUPER_ADMIN && !confFilter && !confProgress) {
      dispatch(subjectSlice.actions.setConferenceFilter(orderedConferences[0]?.id))
    }
  }, [confProgress, confFilter])

  useEffect(() => {
    dispatch(subjectSlice.actions.setProgress(true))
    const unsub = getSubjects(
      user?.role === Roles.SUPER_ADMIN ? confFilter : user?.conferenceId,
      (data) => {
        dispatch(subjectSlice.actions.setSubjects(caseInsensitiveSort(data, 'email')))
      }
    )
    return () => unsub && unsub()
  }, [dispatch, user?.conferenceId, confFilter])

  useEffect(() => {
    if (subjects?.length && speakerCanUploadTalks) {
      const approvedSpeakers = subjects?.filter(
        (e) => e.type === SubjectType.SPEAKER && e.status === SubjectStatus.APPROVED
      )
      const speakerIds = approvedSpeakers?.map((subject) => subject?.id)
      dispatch(fetchMultipleSubjectTalks(speakerIds))
    }
  }, [subjects])

  useEffect(() => {
    if (!inProgress && subjects.length && !areAllValuesZero(subjectCount) && !status) {
      if (subjectCount.pending > 0) {
        dispatch(subjectSlice.actions.setSubjectStatusFilter(SubjectStatus.INVITE_NOT_SENT))
      } else if (subjectCount.inReview > 0) {
        dispatch(subjectSlice.actions.setSubjectStatusFilter(SubjectStatus.PENDING_DATA_REVIEW))
      } else if (subjectCount.invited > 0) {
        dispatch(subjectSlice.actions.setSubjectStatusFilter(SubjectStatus.AWAITING_RESPONSE))
      } else if (subjectCount.approved > 0) {
        dispatch(subjectSlice.actions.setSubjectStatusFilter(SubjectStatus.APPROVED))
      } else {
        dispatch(subjectSlice.actions.setSubjectStatusFilter('All'))
      }
    }

    if (!inProgress && (!subjects.length || areAllValuesZero(subjectCount)) && !status) {
      dispatch(subjectSlice.actions.setSubjectStatusFilter('All'))
    }
  }, [inProgress, subjects.length])

  const handleInviteSubject = () => {
    const commonURL = `${location.pathname}/invite?type=${subjectType}`
    const url =
      user.role === Roles.SUPER_ADMIN ? `${commonURL}&conferenceId=${confFilter}` : `${commonURL}`

    history.push(url)
  }

  const handleCreateSubject = () => {
    if (subjectType === SubjectType.EXHIBITOR) history.push('/admin/exhibitors/new')
    else if (subjectType === SubjectType.SPEAKER) history.push('/admin/speakers/new')
    else history.push('/admin/sponsors/new')
  }

  return (
    <Box>
      <ConfirmModal
        dialogTitle={`Send ${
          status === SubjectStatus.INVITE_NOT_SENT ? 'Invitation' : 'Reminder'
        } Emails`}
        dialogText={
          status === SpeakerSubjectFilter.AWAITING_TALKS
            ? `This will send Reminder email(s) to ${awaitingForTalks?.length} Speakers to submit their talks.`
            : `This will send ${
                status === SubjectStatus.INVITE_NOT_SENT ? 'Invitation' : 'Reminder'
              } email(s) to ${
                status === SubjectStatus.INVITE_NOT_SENT ? `${inviteCount} pending` : reminderCount
              } ${subjectType}(s).`
        }
        open={openEmailModal}
        inProgress={loading}
        closeModal={handleCloseEmailModal}
        handleConfirm={handleSendInviteEmails}
      />
      <Box className={classes.headBar}>
        <Typography variant="h4">Subject Invites</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {user.role === Roles.SUPER_ADMIN ? (
            <Box className={classes.filterDropdown}>
              <Typography className={classes.filterLabel}>Conference: </Typography>
              {confProgress ? (
                <CircularProgress size={25} />
              ) : (
                <FormControl fullWidth variant="outlined" size="small" className={classes.select}>
                  <Select
                    value={confFilter}
                    onChange={(event) => {
                      dispatch(subjectSlice.actions.setConferenceFilter(event.target.value))
                      dispatch(subjectSlice.actions.setSubjectStatusFilter(''))
                    }}>
                    {orderedConferences.map((conf) => (
                      <MenuItem value={conf?.id} key={conf?.id}>
                        {conf?.confName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          ) : subjectType !== SubjectType.ATTENDEE ? (
            <Button variant="contained" color="primary" size="large" onClick={handleCreateSubject}>
              {subjectType === SubjectType.EXHIBITOR
                ? 'Create Exhibitor'
                : subjectType === SubjectType.SPEAKER
                ? 'Create Speaker'
                : 'Create Sponsor'}
            </Button>
          ) : null}

          <Button variant="contained" color="primary" size="large" onClick={handleInviteSubject}>
            {subjectType === SubjectType.ATTENDEE
              ? 'Invite Attendee'
              : subjectType === SubjectType.EXHIBITOR
              ? 'Invite Exhibitor'
              : subjectType === SubjectType.SPEAKER
              ? 'Invite Speaker'
              : 'Invite Sponsor'}
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              if (confFilter) {
                history.push({
                  pathname: `${location.pathname}/new`,
                  search: `?conferenceId=${confFilter}`
                })
              } else {
                history.push({
                  pathname: `${location.pathname}/new`
                })
              }
            }}>
            Import Subjects
          </Button>
        </Box>
      </Box>

      <Box className={classes.dashboard}>
        <Typography variant="h4" className={classes.cardsInfo}>
          Click a Card to list down Subjects
        </Typography>
        <Box className={classes.cards}>
          <SubjectCard
            title="Exhibitors"
            subjectCount={exhibitorsCount}
            type={SubjectType.EXHIBITOR}
            selectedType={subjectType}
          />
          <SubjectCard
            title="Speakers"
            subjectCount={speakersCount}
            type={SubjectType.SPEAKER}
            selectedType={subjectType}
            speakerCanUploadTalks={speakerCanUploadTalks}
          />
          <SubjectCard
            title="Sponsors"
            subjectCount={sponsorsCount}
            type={SubjectType.SPONSOR}
            selectedType={subjectType}
          />

          <AttendeeSubjectCard
            title="Attendees"
            subjectCount={attendeesCount}
            type={SubjectType.ATTENDEE}
            selectedType={subjectType}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '1.75rem'
        }}>
        <Typography className={classes.head}>{subjectType}s</Typography>
        <Box className={classes.actions}>
          {status === SubjectStatus.INVITE_NOT_SENT && showInviteEmailButton && (
            <Button variant="contained" color="primary" size="large" onClick={handleOpenEmailModal}>
              Send Invite to All Pending {subjectType}s
            </Button>
          )}
          {status === SubjectStatus.AWAITING_RESPONSE && showReminderEmailButton && (
            <Button variant="contained" color="primary" size="large" onClick={handleOpenEmailModal}>
              Send Reminders To All Waiting For Data
            </Button>
          )}
          {status === SpeakerSubjectFilter.AWAITING_TALKS && subjectCount?.awaiting > 0 && (
            <Button variant="contained" color="primary" size="large" onClick={handleOpenEmailModal}>
              Send Reminders To All Waiting For Talks
            </Button>
          )}
          <Box className={classes.filterDropdown}>
            <Typography className={classes.filterLabel}>Filter by: </Typography>
            <FormControl fullWidth variant="outlined" size="small" className={classes.select}>
              <Select
                value={status}
                onChange={(event) =>
                  dispatch(subjectSlice.actions.setSubjectStatusFilter(event.target.value))
                }>
                {statusList.map((status) => (
                  <MenuItem value={status.id} key={status.id}>
                    {status.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>
      <Wrapper inProgress={inProgress}>
        <Paper variant="outlined" style={{ marginTop: '16px' }}>
          {filteredSubjects.length && !inProgress ? (
            <BasicTable
              data={formatTableData(filteredSubjects)}
              columns={tableHead(subjectType)}
              options={{
                ...options,
                onChangePage: (page) => dispatch(subjectSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(subjectSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          ) : (
            <ResourceNotFound message={`No ${subjectType}s available for this conference`} />
          )}
        </Paper>
      </Wrapper>
    </Box>
  )
}

export default SubjectsListing

const useStyles = makeStyles({
  headBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '1rem'
  },
  head: {
    fontSize: '20px',
    fontWeight: 'bold'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  select: {
    minWidth: '200px'
  },
  filterDropdown: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    justifyContent: 'flex-end'
  },
  filterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '8px',
    fontWeight: 'bold'
  },
  cards: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px'
  },
  cardsInfo: {
    color: '#616161',
    fontSize: '14px'
  },
  dashboard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '1.5rem',
    padding: '1rem',
    borderRadius: '1rem',
    border: '1px solid #bdbdbd'
  }
})
